/**
 * "This match reads the layer stack" — shown at the top of an Insights Lab
 * surface whose queries go through the playground (/api/v1/playground/*),
 * when the server says the game is served from the stack's serve layer
 * (`served_from` on the table list). Nothing is inferred here: no flag, no
 * notice. Other tabs (dashboard, segments, builder) read other endpoints,
 * so they do not show it.
 */
import { useEffect, useState } from 'react'

import { cachedTables, fetchTables } from '../lib/tablesCache'

type Served = { served_from?: 'stack' | 'legacy'; tables?: unknown[] }

export default function StackDataNotice({ gameCode, orgId, what }: {
  gameCode: string | null
  orgId: number | null
  /** What on this surface reads the serve layer, e.g. "The canvas, table
   *  list and assistant". */
  what: string
}) {
  const [served, setServed] = useState<Served | null>(null)
  useEffect(() => {
    setServed(null)
    if (!gameCode || !orgId) return
    let alive = true
    const seen = cachedTables<Served>(gameCode, orgId)
    if (seen) setServed(seen)
    fetchTables<Served>(gameCode, orgId)
      .then((d) => { if (alive) setServed(d) })
      .catch(() => { /* no list, no claim */ })
    return () => { alive = false }
  }, [gameCode, orgId])

  if (served?.served_from !== 'stack') return null
  const n = served.tables?.length ?? 0
  return (
    <div className="lab-notice" role="status" data-testid="stack-data-notice">
      <strong>Stack data.</strong> {what} for {gameCode} read the layer
      stack&rsquo;s serve layer ({n} table{n === 1 ? '' : 's'}), not the
      pre &lsquo;layers&rsquo; analytics.
    </div>
  )
}
